import {
  formatLiveIn,
  formatShortDate,
  formatTime,
  type TVUpcomingMatch,
} from '@/utils/tv-mode-data';
import { TV_PALETTE } from './tv-mode.constants';
import { TVAutoScrollViewport } from './TVAutoScrollViewport';
import { useTVScale } from './TVScaleContext';
import { TVTeamAvatarStack } from './TVTeamAvatarStack';

interface Props {
  matches: TVUpcomingMatch[];
  accent: string;
}

const SecHeader = ({ accent }: { accent: string }) => {
  const { s } = useTVScale();
  return (
    <div
      style={{
        paddingBottom: s(12),
        marginBottom: s(12),
        borderBottom: `2px solid ${accent}`,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontSize: s(10),
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: accent,
          marginBottom: s(4),
          display: 'flex',
          alignItems: 'center',
          gap: s(6),
        }}
      >
        <span
          className="tv-anim-pulse"
          style={{
            display: 'inline-block',
            width: s(7),
            height: s(7),
            borderRadius: '50%',
            background: accent,
          }}
        />
        Agenda
      </div>
      <div
        className="font-gilroy"
        style={{
          fontSize: s(21),
          fontWeight: 900,
          letterSpacing: '0.02em',
          color: TV_PALETTE.navy,
          lineHeight: 1,
        }}
      >
        PRÓXIMOS PARTIDOS
      </div>
    </div>
  );
};

export const TVUpcomingSidebar = ({ matches, accent }: Props) => {
  const { s } = useTVScale();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <SecHeader accent={accent} />
      {matches.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TV_PALETTE.textMuted,
            fontSize: s(12),
            textAlign: 'center',
            padding: s(12),
          }}
        >
          Sin partidos próximos agendados
        </div>
      ) : (
        <TVAutoScrollViewport>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: s(8),
            }}
          >
            {matches.map((m) => {
              const liveIn = formatLiveIn(m.startDate);
              const dateLabel = formatShortDate(m.startDate);
              return (
                <div
                  key={m.id}
                  style={{
                    background: TV_PALETTE.card,
                    borderRadius: 8,
                    padding: `${s(11)}px ${s(14)}px`,
                    border: `1px solid ${liveIn ? TV_PALETTE.accent2 : TV_PALETTE.border}`,
                    boxShadow: liveIn
                      ? '0 0 14px rgba(255,90,29,0.1)'
                      : '0 1px 4px rgba(2,51,115,0.05)',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: s(8),
                      gap: s(8),
                    }}
                  >
                    <div>
                      {dateLabel && (
                        <div
                          style={{
                            fontSize: s(10),
                            color: TV_PALETTE.textMuted,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            lineHeight: 1,
                            marginBottom: s(3),
                          }}
                        >
                          {dateLabel}
                        </div>
                      )}
                    <div
                      className="font-space"
                      style={{
                        fontSize: s(28),
                        fontWeight: 700,
                        lineHeight: 1,
                        letterSpacing: '-0.01em',
                        color: TV_PALETTE.navy,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatTime(m.startDate)}
                    </div>
                    <div
                      style={{
                        fontSize: s(9),
                        color: TV_PALETTE.textMuted,
                        fontWeight: 600,
                        marginTop: 3,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: s(170),
                      }}
                    >
                      {m.court}
                    </div>
                  </div>
                  {liveIn ? (
                    <div
                      className="tv-anim-pulse"
                      style={{
                        background: TV_PALETTE.accent2,
                        color: '#fff',
                        fontSize: s(9),
                        fontWeight: 800,
                        padding: `${s(3)}px ${s(9)}px`,
                        borderRadius: 4,
                        letterSpacing: '0.06em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      LIVE IN {liveIn}
                    </div>
                  ) : (
                    <div
                      style={{
                        background: TV_PALETTE.borderSoft,
                        color: TV_PALETTE.textMuted,
                        fontSize: s(12),
                        fontWeight: 700,
                        padding: `${s(4)}px ${s(10)}px`,
                        borderRadius: 4,
                        border: `1px solid ${TV_PALETTE.border}`,
                        whiteSpace: 'nowrap',
                        maxWidth: s(140),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {m.category}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: s(4),
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: s(8),
                      minWidth: 0,
                    }}
                  >
                    <TVTeamAvatarStack slots={m.team1Slots} size={s(24)} />
                    <span
                      style={{
                        fontSize: s(12),
                        fontWeight: 700,
                        color: TV_PALETTE.navy,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {m.team1}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: s(9),
                      color: TV_PALETTE.textSubtle,
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      textAlign: 'center',
                    }}
                  >
                    VS
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: s(8),
                      minWidth: 0,
                    }}
                  >
                    <TVTeamAvatarStack slots={m.team2Slots} size={s(24)} />
                    <span
                      style={{
                        fontSize: s(12),
                        fontWeight: 700,
                        color: TV_PALETTE.navy,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {m.team2}
                    </span>
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        </TVAutoScrollViewport>
      )}
    </div>
  );
};
